import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import {
  addUser,
  getPermission,
  getUsersList,
  updateUser,
  userStatusChange,
} from "../../Services/UserApi";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as AddIcon } from "../../Assets/addIcon.svg";
import { ReactComponent as EditIcon } from "../../Assets/EditIcon.svg";
import { ReactComponent as ActionIcon } from "../../Assets/actionIcon.svg";
import { ReactComponent as HastagIcon } from "../../Assets/hashtag.svg";
import { ReactComponent as UserNameIcon } from "../../Assets/username.svg";
import { ReactComponent as EmailIcon } from "../../Assets/EmailIcon.svg";
import { ReactComponent as RoleIcon } from "../../Assets/role.svg";
import { ReactComponent as StatusIcon } from "../../Assets/status.svg";
import { toast, ToastContainer } from "react-toastify";
import { Constants } from "../../Constants/CommonConstant";
import { ReactComponent as EyeIcon } from "./../../Assets/eye.svg";
import { ReactComponent as EyehideIcon } from "./../../Assets/eye_hide.svg";
import Grid from "@mui/material/Grid2";

function User() {
  const [loading, setLoading] = useState(true);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [openTogglepopup, setTogglepopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [permissions, setPermissions] = useState<any[]>([]);
  const roles = Object.values(Constants.ROLES).map((role) => role.name);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    role: Yup.string().required("Role is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters"),
  });

  const formik = useFormik({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      role_id: "",
      password: generatePassword(),
      environment: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        let response;
        const selectedRole = Object.values(Constants.ROLES).find(
          (role) => role.name === values.role
        );
        const role_id = selectedRole ? selectedRole.id : "";
        const permissionIds = permissions
          .filter((perm) => perm.granted)
          .map((perm) => perm.id);

        const formData = new FormData();
        formData.append("first_name", values.firstName);
        formData.append("last_name", values.lastName);
        formData.append("email", values.email);
        formData.append("role_id", String(role_id));

        values.environment.forEach((env) => {
          formData.append("environments[]", env);
        });

        if (isEditMode) {
          permissionIds.forEach((permId) => {
            formData.append("permissions[]", permId);
          });
          response = await updateUser(formData, values.id);
        } else {
          formData.append("password", values.password);
          response = await addUser(formData);
        }

        if (response && !response.error) {
          toast.success(
            isEditMode ? "User updated successfully" : "User added successfully"
          );
          setOpen(false);
          formik.resetForm();
          fetchUsers();
        } else {
          toast.error(response.error || "Error saving user");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error("Error while submitting the form");
      }
    },
  });

  function generatePassword() {
    return Math.random().toString(36).slice(-14);
  }

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, orderBy, order]);

  const fetchUsers = async () => {
    setLoading(true);
    const result = await getUsersList(page + 1, rowsPerPage, orderBy, order);
    setLoading(false);

    if (result && !result.error && result.users && result.success === true) {
      setUsersData(result.users);
      setTotalUsers(result.totalUsers);
    } else {
      setError(result.error || "Failed to fetch users");
    }
  };

  const handleOpenDialog = (user: any) => {
    const updatedUser = { ...user, is_active: !user.is_active };
    setSelectedUser(updatedUser);
    setTogglepopup(true);
  };

  const handleClose = () => {
    setTogglepopup(false);
    setSelectedUser(null);
  };

  const handleConfirmToggle = async () => {
    try {
      const updatedStatus = selectedUser.is_active;

      const response = await userStatusChange(selectedUser.id, updatedStatus);

      if (response && !response.error && response.success === true) {
        const updatedData = usersData.map((post: any) =>
          post.key === selectedUser.id
            ? { ...post, is_active: updatedStatus }
            : post
        );
        setUsersData(updatedData);
        toast.success(response.message || "User status updated successfully");
        handleClose();
        fetchUsers();
      } else {
        toast.error(response.error || "Error updating post status");
      }
    } catch (error) {
      toast.error("Error while toggling post status");
      console.error("Error while toggling post status:", error);
    }
  };

  const handleEditUser = async (record: any) => {
    setIsEditMode(true);

    // Set form values
    formik.setValues({
      id: record.id || "",
      firstName: record.first_name || "",
      lastName: record.last_name || "",
      password: record.password || "",
      email: record.email || "",
      role: record.role_name || "",
      role_id: record.role_id || "",
      environment: Array.isArray(record.environments)
        ? record.environments
        : [],
    });

    if (record.id) {
      const result = await getPermission(record.id);
      if (result.success === true) {
        setPermissions(result.permissions);
      } else {
        console.error("Failed to fetch permissions:", result);
      }
    }
    setOpen(true);
  };

  const handlePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleGeneratePassword = () => {
    formik.setFieldValue("password", generatePassword());
  };
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenAddDialog = () => {
    setIsEditMode(false);
    formik.resetForm();
    setOpen(true);
  };
  const handlePermissionChange = (id: number, granted: boolean) => {
    setPermissions((prev) =>
      prev.map((perm) => (perm.id === id ? { ...perm, granted } : perm))
    );
  };

  return (
    <div className="userList">
      <div className="userList__heading">
        <h2>User List</h2>
        <Button
          variant="contained"
          className="userbutton"
          onClick={handleOpenAddDialog}
        >
          <span className="Icon">
            <AddIcon />
          </span>{" "}
          Add User
        </Button>
      </div>
      <Dialog
        className="modal"
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <h2 className="mb-0"> {isEditMode ? "Edit User" : " Add User"}</h2>
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="modal_body">
            <Grid
              container
              alignItems={"center"}
              spacing={{ xs: 0, sm: 1, md: 2 }}
            >
              <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                <label>
                  First Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  placeholder="First Name"
                  name="firstName"
                  variant="filled"
                  fullWidth
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                <label>
                  Last Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  placeholder="Last Name"
                  name="lastName"
                  variant="filled"
                  fullWidth
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={{ xs: 0, sm: 1, md: 2 }}
              alignItems={"center"}
            >
              <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                <label>
                  Email <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  placeholder="Email"
                  name="email"
                  variant="filled"
                  type="email"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={
                    formik.touched.email &&
                    typeof formik.errors.email === "string"
                      ? formik.errors.email
                      : undefined
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                <label>
                  Role <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  placeholder="Role"
                  name="role"
                  variant="filled"
                  fullWidth
                  select
                  value={formik.values.role || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={{ xs: 0, sm: 1, md: 2 }}
              alignItems={"center"}
            >
              <Grid size={{ xs: 12, md: 6, sm: 6 }} mb={0}>
                <label>Environment</label>
                <TextField
                  placeholder="Environment"
                  name="environment"
                  variant="filled"
                  fullWidth
                  select
                  value={formik.values.environment || []}
                  onChange={formik.handleChange}
                  SelectProps={{
                    multiple: true,
                  }}
                  error={
                    formik.touched.environment &&
                    Boolean(formik.errors.environment)
                  }
                  helperText={
                    formik.touched.environment && formik.errors.environment
                  }
                  sx={{
                    marginBottom: "0px",
                  }}
                >
                  <MenuItem value="sandbox">Sandbox</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6, sm: 6 }} mb={0}>
                <div>
                  {!isEditMode && (
                    <>
                      <label>
                        Password <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        placeholder="Password"
                        name="password"
                        fullWidth
                        variant="filled"
                        type={showPassword ? "text" : "password"}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        sx={{
                          marginBottom: "0px",
                          "& .MuiFilledInput-root": {
                            backgroundColor: "#fff",
                            paddingRight:'0px'
                          },
                        }}
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password && formik.errors.password
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box className="generate">
                                <Button
                                  onClick={handleGeneratePassword}
                                  color="error"
                                  size="small"
                                >
                                  Generate
                                </Button>
                                <IconButton
                                  className="realtive-eye"
                                  onClick={handlePasswordVisibility}
                                >
                                  {showPassword ? <EyeIcon /> : <EyehideIcon />}
                                </IconButton>
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}
                </div>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={{ xs: 0, sm: 1, md: 2 }}
              alignItems={"center"}
            >
              {permissions && isEditMode && (
                <>
                  <Grid size={{ xs: 12, md: 3 }} mb={2}>
                    <h4>User Permissions:</h4>
                  </Grid>

                  {permissions?.map((perm: any) => (
                    <Grid key={perm.id} size={{ xs: 12, md: 4 }} mb={2}>
                      <Box
                        sx={{
                          background: "rgba(249, 249, 249, 1)",
                          border: "1px solid rgba(196, 196, 196, 1)",
                          padding: "5px",
                          display: "inline-block",
                        }}
                      >
                        <FormControlLabel
                          className="check_box mt-2"
                          value="end"
                          control={
                            <Checkbox
                              checked={perm.granted}
                              onChange={(e) =>
                                handlePermissionChange(
                                  perm.id,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={perm.title}
                          labelPlacement="end"
                        />
                      </Box>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
            <DialogActions disableSpacing className="modal_footer--button">
              <Button
                className="button"
                sx={{
                  "--variant-containedBg": "rgba(228, 0, 43, 1)",
                }}
                variant="contained"
                color="error"
                type="submit"
              >
                {isEditMode ? "Update User" : " Add User"}
              </Button>
            </DialogActions>
          </DialogContent>
        </form>
      </Dialog>
      <Dialog
        open={openTogglepopup}
        onClose={handleClose}
        className="modal"
        maxWidth="md"
      >
        <h2 className="mb-0">
          {" "}
          {!selectedUser?.is_active ? "Deactivate User" : "Activate User"}
        </h2>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent className="modal_body">
          <DialogContentText>
            <p>
              Are you sure you want to{" "}
              <b> {!selectedUser?.is_active ? "deactivate" : "activate"} </b>{" "}
              this user?
            </p>{" "}
            <p>This action will change the user’s status.</p>
          </DialogContentText>
        </DialogContent>
        <DialogActions disableSpacing className="modal_footer--button">
          <Button
            className="button"
            variant="outlined"
            onClick={handleClose}
            color="secondary"
            sx={{
              "--variant-outlinedBorder": "rgba(228, 0, 43, 1)",
              "--variant-outlinedColor": "rgba(228, 0, 43, 1)",
              "--variant-containedBg": "rgba(228, 0, 43, 1)",

              "&:hover": {
                "--variant-outlinedBorder": "rgba(228, 0, 43, 1)",
                "--variant-outlinedBg": "rgba(228, 0, 43, 0.04)",
                "--variant-containedBg": "rgba(228, 0, 43, 1)",
                "--variant-textBg": "rgba(228, 0, 43, 0.04)",
              },
            }}
          >
            No
          </Button>
          <Button
            className="button"
            variant="contained"
            onClick={() => handleConfirmToggle()}
            color="primary"
            autoFocus
            sx={{
              "--variant-containedBg": "rgba(228, 0, 43, 1)",
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <div className="userList__table">
        <TableContainer sx={{ maxHeight: "80vh" }}>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <span>
                    <HastagIcon />
                  </span>{" "}
                  S.No
                </TableCell>
                <TableCell>
                  {" "}
                  <span>
                    <UserNameIcon />
                  </span>{" "}
                  Name
                </TableCell>
                <TableCell>
                  {" "}
                  <span>
                    <EmailIcon />
                  </span>{" "}
                  Email
                  <TableSortLabel
                    active={orderBy === "email"}
                    direction={order}
                    onClick={() => handleRequestSort("email")}
                  ></TableSortLabel>
                </TableCell>
                <TableCell>
                  {" "}
                  <span>
                    <RoleIcon />
                  </span>{" "}
                  Role
                </TableCell>
                <TableCell>
                  {" "}
                  <span>
                    <StatusIcon />
                  </span>{" "}
                  Status
                </TableCell>
                <TableCell>
                  {" "}
                  <span>
                    <ActionIcon />
                  </span>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersData.map((row: any, index: any) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{`${row.first_name} ${row.last_name}`}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.role_name}</TableCell>
                  <TableCell>
                    <div className="switch-wrapper">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={row.is_active}
                          onChange={() => handleOpenDialog(row)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditUser(row)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default User;
